import { useMemo, useEffect, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Icon, Modal } from 'components'
import { UploadHint } from 'components/UploadHint'
import { SetLocationModal, activeCooldown } from 'components/regionTeams/SetLocationModal'
import { locationDisplayName } from 'constants/geo/regionLabels'

import { useApp } from 'store/app/hooks'
import { useAuth } from 'store/auth/hooks'

import { track } from 'utils/analytics'
import { UpdateProfileInput } from './types'
import './ProfileEditModal.css'

// Keep in sync with the backend bio cap (user.controller.ts MAX_BIO_LENGTH).
const MAX_BIO_LENGTH = 500

export const ProfileEditModal = () => {
  const { isLoading, user, error: authError, updateUser, message, messageCleanUp } = useAuth()
  const { isProfileEditModalOpen, setIsProfileEditModalOpen } = useApp()
  const [usernameError, setUsernameError] = useState<string>('')
  const [showSaved, setShowSaved] = useState<boolean>(false)
  const [isLocationOpen, setIsLocationOpen] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm<UpdateProfileInput>({
    defaultValues: useMemo(() => {
      return {
        firstname: user?.firstname,
        lastname: user?.lastname,
        username: user?.username,
        biography: user?.biography,
      }
    }, [user]),
  })

  const onSubmit = async (data: UpdateProfileInput): Promise<void> => {
    setUsernameError('')
    console.log('🚀 Form submitted - starting save...')

    // Only send files if they are actual File objects, not FileList or undefined
    const submitData = {
      ...data,
      avatar: data.avatar instanceof File ? data.avatar : undefined,
      cover_photo: data.cover_photo instanceof File ? data.cover_photo : undefined,
    }

    try {
      await updateUser(submitData as UpdateProfileInput)
      console.log('✅ Save complete!')
    } catch (error) {
      console.error('❌ Save failed:', error)
    }
  }

  // Track previous loading state to detect when save completes
  const [prevLoading, setPrevLoading] = useState(false)
  const [hasShownSuccess, setHasShownSuccess] = useState(false)

  useEffect(() => {
    console.log('📊 State changed - isLoading:', isLoading, 'authError:', authError, 'message:', message, 'modalOpen:', isProfileEditModalOpen)

    // ONLY process if the modal is actually open - prevents toast on page refresh
    if (!isProfileEditModalOpen) {
      console.log('⏭️ Modal is closed, skipping state processing to prevent unwanted toasts')
      return
    }

    // Detect when loading transitions from true to false (save completed)
    if (prevLoading && !isLoading && !hasShownSuccess) {
      console.log('🎯 Save completed! Checking for errors...')

      // Check for errors
      const errorMessage = authError?.toString() || ''
      const hasError = errorMessage || (message && !message.toLowerCase().includes('success'))

      if (hasError) {
        const fullErrorMessage = errorMessage || message || ''
        console.log('❌ Error detected:', fullErrorMessage)

        // Handle username errors
        if (fullErrorMessage.toLowerCase().includes('username') ||
            fullErrorMessage.toLowerCase().includes('taken') ||
            fullErrorMessage.toLowerCase().includes('reserved')) {
          setUsernameError(fullErrorMessage)
          toast.error(fullErrorMessage, {
            position: "top-center",
            autoClose: 5000,
          })
        } else if (fullErrorMessage) {
          toast.error(fullErrorMessage, {
            position: "top-center",
            autoClose: 5000,
          })
        }
      } else {
        // SUCCESS! No errors detected
        console.log('✅ SUCCESS! Showing success feedback...')
        track('profile_updated')

        // Mark that we've shown success to prevent duplicate toasts
        setHasShownSuccess(true)

        // Clear the message from Redux immediately to prevent re-triggering
        messageCleanUp()

        // Show success button state
        setShowSaved(true)

        // Show BIG SUCCESS toast
        toast.success('✅ Profile Updated Successfully!', {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          theme: "colored",
        })

        // Close modal after showing success for 1.5 seconds
        const timer = setTimeout(() => {
          console.log('🚪 Closing modal...')
          setShowSaved(false)
          setIsProfileEditModalOpen(false)
          setUsernameError('')
        }, 1500)

        return () => clearTimeout(timer)
      }
    }

    // Update previous loading state
    setPrevLoading(isLoading)
  }, [isLoading, authError, message, prevLoading, hasShownSuccess, setIsProfileEditModalOpen, isProfileEditModalOpen])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isProfileEditModalOpen) {
      setShowSaved(false)
      setHasShownSuccess(false)
      setUsernameError('')
      setPrevLoading(false)
    } else {
      // When modal opens, reset all success/error states and form to current user data
      setHasShownSuccess(false)
      setShowSaved(false)
      setUsernameError('')
      setPrevLoading(false)
      reset({
        firstname: user?.firstname,
        lastname: user?.lastname,
        username: user?.username,
        biography: user?.biography,
      })
    }
  }, [isProfileEditModalOpen, user, reset])

  const bioValue = watch('biography') || ''
  const bioLength = bioValue.length
  const bioCounterClass =
    bioLength > MAX_BIO_LENGTH
      ? 'pem-counter over'
      : bioLength > MAX_BIO_LENGTH * 0.9
      ? 'pem-counter warn'
      : 'pem-counter'

  return (
    <Modal
      title="Edit Profile"
      isOpened={isProfileEditModalOpen}
      onClose={() => setIsProfileEditModalOpen(false)}
      withCard={true}
    >
      <div className="pem">
        <div className="pem-rail" />
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ===== Media zone ===== */}
          <div className="pem-media">
            <Controller
              control={control}
              name="cover_photo"
              render={(arg: any) => {
                const coverSrc =
                  arg.field && arg.field.value && arg.field.value.length !== 0
                    ? URL.createObjectURL(arg.field.value)
                    : user?.cover_photo
                    ? user.cover_photo.url
                    : undefined
                return (
                  <div className="pem-banner">
                    {coverSrc && (
                      <img
                        src={coverSrc}
                        className="no-save"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        alt="Profile banner"
                      />
                    )}
                    <span className="pem-corner tl" />
                    <span className="pem-corner tr" />
                    <span className="pem-corner bl" />
                    <span className="pem-corner br" />
                    <label htmlFor="cover-upload" className="pem-banner-cam">
                      <span className="pem-cam">
                        <Icon name="camera" size={20} />
                      </span>
                      <input
                        accept="image/*"
                        className="hidden"
                        id="cover-upload"
                        type="file"
                        {...register('cover_photo')}
                        onChange={(e) => {
                          const fileList = e.target.files
                          if (!fileList) return
                          arg.field.onChange(fileList[0])
                        }}
                      />
                    </label>
                  </div>
                )
              }}
            />
            <Controller
              control={control}
              name="avatar"
              render={(arg: any) => {
                const avatarSrc =
                  arg.field && arg.field.value && arg.field.value.length !== 0
                    ? URL.createObjectURL(arg.field.value)
                    : user?.avatar?.url
                return (
                  <div className="pem-avatar-wrap">
                    <div className="pem-avatar-ring">
                      <img
                        src={avatarSrc}
                        className="no-save"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        alt="Your avatar"
                      />
                    </div>
                    <label htmlFor="avatar-upload" className="pem-avatar-cam">
                      <span className="pem-cam">
                        <Icon name="camera" size={18} />
                      </span>
                      <input
                        accept="image/*"
                        className="hidden"
                        id="avatar-upload"
                        type="file"
                        {...register('avatar')}
                        onChange={(e) => {
                          const fileList = e.target.files
                          if (!fileList) return
                          arg.field.onChange(fileList[0])
                        }}
                      />
                    </label>
                  </div>
                )
              }}
            />
          </div>

          <div className="pem-hints">
            <UploadHint type="banner" />
            <UploadHint type="avatar" />
          </div>

          {/* ===== Identity ===== */}
          <div className="pem-kicker">// Identity</div>

          <div className="pem-row">
            <div className="pem-field">
              <div className="pem-label">
                First name <span className="pem-optional">optional</span>
              </div>
              <div className="pem-input-wrap">
                <span className="pem-input-icon">
                  <Icon name="account" size={16} />
                </span>
                <input
                  className={`pem-input ${errors?.firstname ? 'has-error' : ''}`}
                  placeholder="First name"
                  {...register('firstname')}
                />
              </div>
              {errors?.firstname?.message && (
                <div className="pem-error">{errors.firstname.message}</div>
              )}
            </div>

            <div className="pem-field">
              <div className="pem-label">
                Last name <span className="pem-optional">optional</span>
              </div>
              <div className="pem-input-wrap">
                <span className="pem-input-icon">
                  <Icon name="account" size={16} />
                </span>
                <input
                  className={`pem-input ${errors?.lastname ? 'has-error' : ''}`}
                  placeholder="Last name"
                  {...register('lastname')}
                />
              </div>
              {errors?.lastname?.message && (
                <div className="pem-error">{errors.lastname.message}</div>
              )}
            </div>
          </div>

          <div className="pem-field">
            <div className="pem-label">Username</div>
            <div className="pem-input-wrap">
              <span className="pem-input-icon">
                <Icon name="alias" size={16} />
              </span>
              <input
                className={`pem-input ${
                  errors?.username || usernameError ? 'has-error' : ''
                }`}
                placeholder="Choose a callsign"
                {...register('username', {
                  required: 'Username is required.',
                  minLength: {
                    value: 2,
                    message: 'Username must be at least 2 characters long',
                  },
                  maxLength: {
                    value: 20,
                    message: 'Username cannot exceed 20 characters',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_-]+$/,
                    message:
                      'Letters, numbers, underscores and hyphens only',
                  },
                })}
                onInput={() => {
                  if (usernameError) setUsernameError('')
                }}
              />
            </div>
            {(errors?.username?.message || usernameError) && (
              <div className="pem-error">
                <span>⚠</span>
                <span>{errors?.username?.message || usernameError}</span>
              </div>
            )}
          </div>

          {/* ===== Bio ===== */}
          <div className="pem-field">
            <div className="pem-label">
              <span>Bio</span>
              <span className={bioCounterClass}>
                {bioLength}/{MAX_BIO_LENGTH}
              </span>
            </div>
            <textarea
              className={`pem-textarea ${errors?.biography ? 'has-error' : ''}`}
              rows={3}
              maxLength={MAX_BIO_LENGTH}
              placeholder="Tell the network who you are…"
              {...register('biography', {
                maxLength: {
                  value: MAX_BIO_LENGTH,
                  message: `Bio cannot exceed ${MAX_BIO_LENGTH} characters`,
                },
              })}
            />
            {errors?.biography?.message && (
              <div className="pem-error">{errors.biography.message}</div>
            )}
          </div>

          {/* ===== Location (Region Teams) — saved on its own via PUT /me/location,
               not part of the multipart /me/update form ===== */}
          <div className="pem-field">
            <div className="pem-label">
              <span>Location</span>
              {user?.location?.country && user.location.country === 'US' && (
                <span className="pem-optional">{user.location.public === false ? 'HIDDEN ON PROFILE' : 'SHOWN ON PROFILE'}</span>
              )}
            </div>
            <div className="pem-location">
              <span className={user?.location?.country ? 'pem-location-value' : 'pem-location-empty'}>
                {user?.location?.country ? locationDisplayName(user.location) : 'Not set — needed for region teams tournaments'}
              </span>
              <button type="button" className="pem-location-btn" onClick={() => setIsLocationOpen(true)}>
                {user?.location?.country ? 'Change' : 'Set location'}
              </button>
            </div>
            {activeCooldown(user) && (
              <div className="pem-location-note">
                You can change this again on{' '}
                {new Date(activeCooldown(user) as string).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
            )}
          </div>

          {/* ===== Footer ===== */}
          <div className="pem-footer">
            <button
              type="submit"
              className={`pem-save ${showSaved ? 'is-saved' : ''}`}
              disabled={isLoading || showSaved}
            >
              {showSaved ? (
                'Saved ✓'
              ) : isLoading ? (
                <>
                  <span className="pem-spinner" />
                  Saving
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
      <SetLocationModal isOpen={isLocationOpen} onClose={() => setIsLocationOpen(false)} user={user} />
    </Modal>
  )
}
